(() => {
  const ELEMENT_NAME = "jkl-wishlist-items";
  const SWYM_POLL_INTERVAL_MS = 250;
  const SWYM_MAX_WAIT_MS = 30000;

  const withSwymCallback = (executor) =>
    new Promise((resolve, reject) => {
      try {
        executor(resolve, reject);
      } catch (error) {
        reject(error);
      }
    });

  const firstString = (values) => {
    for (const value of values) {
      if (typeof value !== "string") {
        continue;
      }

      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }

    return "";
  };

  const sanitizeUrl = (value) => {
    const normalized = firstString([value]);
    const lowerCased = normalized.toLowerCase();

    if (!normalized) {
      return "";
    }

    if (
      lowerCased.startsWith("javascript:") ||
      lowerCased.startsWith("data:")
    ) {
      return "";
    }

    return normalized;
  };

  const fetchWishlistItems = (swat) =>
    new Promise((resolve) => {
      if (!swat || typeof swat.fetch !== "function") {
        resolve([]);
        return;
      }

      try {
        swat.fetch((items) => {
          resolve(Array.isArray(items) ? items : []);
        });
      } catch {
        resolve([]);
      }
    });

  const removeWishlistItem = (swat, item) => {
    if (!swat || typeof swat.removeFromWishList !== "function") {
      return Promise.reject(
        new Error("Swym removeFromWishList is unavailable."),
      );
    }

    return withSwymCallback((resolve, reject) => {
      swat.removeFromWishList(
        item,
        () => resolve(),
        (error) => reject(error),
      );
    });
  };

  const waitForSwymGlobal = (maxWaitMs = SWYM_MAX_WAIT_MS) =>
    new Promise((resolve) => {
      if (window._swat) {
        resolve(window._swat);
        return;
      }

      let isDone = false;
      const startedAt = Date.now();
      let pollTimerId = null;

      const finish = (swat) => {
        if (isDone) {
          return;
        }

        isDone = true;
        if (pollTimerId !== null) {
          window.clearInterval(pollTimerId);
        }
        resolve(swat || null);
      };

      const onReady = (swat) => finish(swat || window._swat || null);
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(onReady);

      pollTimerId = window.setInterval(() => {
        if (window._swat) {
          finish(window._swat);
          return;
        }

        if (Date.now() - startedAt >= maxWaitMs) {
          finish(null);
        }
      }, SWYM_POLL_INTERVAL_MS);
    });

  const showWishlistToast = (message, modifier = "success") => {
    if (typeof window.JKLWishlistToast?.show === "function") {
      window.JKLWishlistToast.show(message, modifier);
    }
  };

  const resolveSwym = async () => {
    const libraryGetSwym = window.JKLSwymWishlist?.getSwym;

    if (typeof libraryGetSwym === "function") {
      const swat = await libraryGetSwym().catch(() => null);
      if (swat) {
        return swat;
      }
    }

    return waitForSwymGlobal();
  };

  class JKLWishlistItems extends HTMLElement {
    constructor() {
      super();

      this.swat = null;
      this.items = [];
      this.isInitialized = false;
      this.isRemoving = false;
      this.messageText = "";
      this.handleClick = this.onClick.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) {
        this.addEventListener("click", this.handleClick);
        return;
      }

      this.isInitialized = true;
      const globalWishlistStrings = window.JKLWishlistI18n || {};
      this.loadingText = this.dataset.loadingText || "";
      this.emptyText = this.dataset.emptyText || "";
      this.errorText = this.dataset.errorText || "";
      this.removeText = this.dataset.removeText || "";
      this.removeWithTitleText = this.dataset.removeWithTitleText || "";
      this.removingText = this.dataset.removingText || "";
      this.removedToastText =
        this.dataset.removedToastText ||
        globalWishlistStrings.removedToastText ||
        "";
      this.productText = this.dataset.productText || "";

      this.addEventListener("click", this.handleClick);
      this.initialize();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
    }

    async initialize() {
      this.renderLoading();

      try {
        this.swat = await resolveSwym();
      } catch (error) {
        console.warn("[JKL Wishlist Page] Failed to initialize Swym.", error);
        this.renderError();
        return;
      }

      if (!this.swat) {
        console.warn("[JKL Wishlist Page] Swym is unavailable on this page.");
        this.renderError();
        return;
      }

      try {
        await this.refreshItems();
      } catch (error) {
        console.warn(
          "[JKL Wishlist Page] Failed to fetch wishlist items.",
          error,
        );
        this.renderError();
        return;
      }

      this.renderFromItems();
    }

    async refreshItems() {
      const items = await fetchWishlistItems(this.swat);
      this.items = Array.isArray(items) ? items : [];
    }

    renderFromItems() {
      if (this.items.length === 0) {
        this.renderEmpty();
        return;
      }

      this.renderItems();
    }

    onClick(event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const button = event.target.closest("[data-remove-index]");
      if (!button || !this.contains(button)) {
        return;
      }

      event.preventDefault();

      if (this.isRemoving) {
        return;
      }

      const index = Number(button.getAttribute("data-remove-index"));
      if (!Number.isInteger(index) || index < 0 || index >= this.items.length) {
        return;
      }

      this.removeByIndex(index, button);
    }

    async removeByIndex(index, button) {
      const item = this.items[index];
      if (!item || !this.swat) {
        return;
      }

      this.isRemoving = true;
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      const label = button.querySelector(".jkl-wishlist-items__remove-label");
      if (label) {
        label.textContent = this.removingText;
      }

      try {
        await removeWishlistItem(this.swat, item);
        this.messageText = "";
        if (typeof window.JKLSwymWishlist?.syncHeaderCount === "function") {
          await window.JKLSwymWishlist.syncHeaderCount();
        }
        await this.refreshItems();
        this.renderFromItems();
        showWishlistToast(this.removedToastText, "success");
      } catch {
        this.messageText = this.errorText;
        this.renderItems();
      } finally {
        this.isRemoving = false;
      }
    }

    getItemTitle(item) {
      return (
        firstString([
          item?.cprops?.jkl_product_title,
          item?.dt,
          item?.title,
          item?.product_title,
          item?.name,
        ]) || this.productText
      );
    }

    getItemUrl(item) {
      return sanitizeUrl(
        firstString([
          item?.cprops?.jkl_product_url,
          item?.du,
          item?.url,
          item?.product_url,
          item?.link,
        ]),
      );
    }

    createStatusNode(text, modifier) {
      const status = document.createElement("p");
      status.className = `jkl-wishlist-items__status jkl-wishlist-items__status--${modifier}`;
      status.textContent = text;

      if (modifier === "error") {
        status.setAttribute("role", "alert");
      } else {
        status.setAttribute("aria-live", "polite");
      }

      return status;
    }

    createCard(item, index) {
      const title = this.getItemTitle(item);
      const url = this.getItemUrl(item);
      const imageUrl = firstString([
        item?.cprops?.jkl_product_image_src,
        item?.iu,
      ]);

      const article = document.createElement("article");
      article.className = "jkl-wishlist-items__card";

      const mediaWrapper = document.createElement(url ? "a" : "div");
      mediaWrapper.className = "jkl-wishlist-items__media";

      if (url) {
        mediaWrapper.href = url;
      }

      if (imageUrl) {
        const image = document.createElement("img");
        image.className = "jkl-wishlist-items__image";
        image.src = imageUrl;
        image.alt = title;
        image.loading = "lazy";
        mediaWrapper.appendChild(image);
      } else {
        const placeholder = document.createElement("div");
        placeholder.className = "jkl-wishlist-items__image-placeholder";
        placeholder.textContent = title;
        mediaWrapper.appendChild(placeholder);
      }

      const titleNode = document.createElement(url ? "a" : "p");
      titleNode.className = "jkl-wishlist-items__title";
      titleNode.textContent = title;

      if (url) {
        titleNode.href = url;
      }

      const removeButton = document.createElement("button");
      removeButton.type = "button";
      removeButton.className = "jkl-wishlist-items__remove";
      removeButton.setAttribute("data-remove-index", String(index));
      const removeAriaLabel = this.removeWithTitleText.includes("{{ title }}")
        ? this.removeWithTitleText.replace("{{ title }}", title)
        : `${this.removeText} ${title}`.trim();
      if (removeAriaLabel) {
        removeButton.setAttribute("aria-label", removeAriaLabel);
      }

      const removeIcon = document.createElement("span");
      removeIcon.className = "jkl-wishlist-items__remove-icon";
      removeIcon.setAttribute("aria-hidden", "true");
      removeIcon.textContent = "×";
      removeButton.appendChild(removeIcon);

      const removeLabel = document.createElement("span");
      removeLabel.className = "jkl-wishlist-items__remove-label sr-only";
      removeLabel.textContent = this.removeText;
      removeButton.appendChild(removeLabel);

      article.append(mediaWrapper, titleNode, removeButton);
      return article;
    }

    renderLoading() {
      this.replaceChildren(this.createStatusNode(this.loadingText, "loading"));
    }

    renderError() {
      this.replaceChildren(this.createStatusNode(this.errorText, "error"));
    }

    renderEmpty() {
      this.replaceChildren(this.createStatusNode(this.emptyText, "empty"));
    }

    renderItems() {
      const fragment = document.createDocumentFragment();

      if (this.messageText) {
        fragment.appendChild(this.createStatusNode(this.messageText, "error"));
      }

      const grid = document.createElement("div");
      grid.className = "jkl-wishlist-items__grid";

      this.items.forEach((item, index) => {
        grid.appendChild(this.createCard(item, index));
      });

      fragment.appendChild(grid);
      this.replaceChildren(fragment);
    }
  }

  if (!window.customElements.get(ELEMENT_NAME)) {
    window.customElements.define(ELEMENT_NAME, JKLWishlistItems);
  }
})();
